import { AxiosResponse } from "axios"

import HttpService, { StreamEvents } from "@services/http.service"
import QueryAttachment from "@store/search/query-attachment"
import { DefaultSuccessResponse } from "@services/common/types"

export type CreateChatAttachmentResponse = {
  id: string
  sessionId: string
  text?: string
}

const unwrapAttachmentResponse = (
  response: { data: DefaultSuccessResponse<CreateChatAttachmentResponse> | CreateChatAttachmentResponse }
): CreateChatAttachmentResponse => {
  const body = response.data as DefaultSuccessResponse<CreateChatAttachmentResponse>
  const payload = body?.data ?? (response.data as CreateChatAttachmentResponse)

  if (!payload?.id || !payload?.sessionId) {
    throw new Error("Invalid attachment upload response")
  }

  return payload
}

class ChatAPI extends HttpService {
  search = (
    params: {
      query: string
      avatar: string
      sessionId: string
      productSolutionId: string
    },
    events: StreamEvents
  ): Promise<void> => {
    return this.postJSONStream("ts/chat/query", params, events)
  }

  createAttachment = async (params: {
    attachment: QueryAttachment
    sessionId?: string
  }): Promise<CreateChatAttachmentResponse> => {
    const data = new FormData()
    data.set("attachment", params.attachment.file)

    if (params.sessionId) {
      data.set("sessionId", params.sessionId)
    }

    const response = await this.post("ts/chat/attachment", data, true)

    return unwrapAttachmentResponse(response)
  }

  answerDocument = async (params: {
    fileName: string
    question: string
    extraction: string
  }): Promise<string> => {
    const response = await this.post("ts/chat/document-answer", params, true)
    const body = response.data as DefaultSuccessResponse<{ answer: string }> | { answer: string }
    const payload = (body as DefaultSuccessResponse<{ answer: string }>)?.data ?? (body as { answer: string })
    if (!payload?.answer) throw new Error("Could not answer from the document")
    return payload.answer
  }

  sharePointSources = async (question: string): Promise<string> => {
    const response = await this.post("ts/chat/sharepoint-sources", { question }, true)
    const body = response.data as DefaultSuccessResponse<{ answer: string }> | { answer: string }
    const payload = (body as DefaultSuccessResponse<{ answer: string }>)?.data ?? (body as { answer: string })
    return payload?.answer || ""
  }

  /** LAB-113: the passage and page of each cited SharePoint document that supports the claims beside its marker. */
  citationPassages = async (
    citations: { url: string; claims: string[] }[]
  ): Promise<{ url: string; found: boolean; excerpt?: string; page?: number }[]> => {
    const response = await this.post("ts/chat/citation-passages", { citations }, true)
    type Passages = { passages: { url: string; found: boolean; excerpt?: string; page?: number }[] }
    const body = response.data as DefaultSuccessResponse<Passages> | Passages
    const payload = (body as DefaultSuccessResponse<Passages>)?.data ?? (body as Passages)
    return payload?.passages || []
  }

  readImage = async (file: File, question: string): Promise<CreateChatAttachmentResponse> => {
    const data = new FormData()
    data.set("attachment", file)
    if (question.trim()) data.set("question", question.trim())
    const response = await this.post("ts/chat/attachment", data, true)
    return unwrapAttachmentResponse(response)
  }

  deleteAttachment = (params: {
    id: string
    sessionId: string
  }): Promise<AxiosResponse> => {
    return this.delete("ts/chat/attachment", undefined, true, params)
  }
}

export default new ChatAPI()
